import { TicketPercent, ArrowLeftRight, FileCheck2, BookOpen, Blocks, Cable, Handshake, Layers, PenTool, Building2, Check, ChevronsUpDown, History, Plus, ShieldAlert, ShieldCheck, Code2, Database, FileClock, Gauge, Inbox, LayoutGrid, LogOut, Mail, Menu, Moon, Palette, RefreshCcw, Rocket, SlidersHorizontal, Sparkles, Sun, UserSearch, Users, Workflow, Zap, Landmark, Import } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/auth";
import { useOrganization, usePartnersEnabled } from "../api/queries";
import { StatusBadge } from "./StatusBadge";
import { useOrg } from "../api/org";
import { useLuxWallet } from "../api/queries";
import { fmtLux } from "../lib/lux";
import { useOrgPlan } from "../pages/admin/OrgPlan";
import { initials } from "../lib/format";
import { NamePrompt } from "./NamePrompt";
import { HelpLink } from "./HelpLink";
import { Button, cx } from "./ui";
import { LuxCoin, LuxGlyph } from "./LuxIcon";
import { config, landingUrl } from "../config";
import { LIBRARY_MENU } from "../library/menu";
import { APP_BUILT_AT, APP_COMMIT, APP_VERSION, versionLabel } from "../version";
import { useT } from "../i18n/I18nProvider";
import { DirIcon } from "../i18n/mirror";
import { LanguagePicker, useLocaleSync } from "../i18n/LanguagePicker";
import { FormattedMessage } from "react-intl";

export function useTheme(): [string, () => void] {
  const [theme, setTheme] = useState(() => document.documentElement.dataset.theme || "light");
  const toggle = () => {
    const next = theme === "dark" ? "light" : "dark";
    if (next === "dark") document.documentElement.dataset.theme = "dark";
    else delete document.documentElement.dataset.theme;
    try {
      localStorage.setItem("solaros.theme", next);
    } catch {
      /* private mode */
    }
    setTheme(next);
  };
  return [theme, toggle];
}

/** The SolarOS logo: opens the marketing site (landingUrl) in the same tab. */
export function Brand({ sub }: { sub?: string }) {
  const t = useT();
  return (
    <a href={landingUrl()} className="brand" title={t("shell.brand.website")}>
      <img src="/solaros-mark.png" alt="" />
      <div>
        <b>SolarOS</b>
        {sub && <small>{sub}</small>}
      </div>
    </a>
  );
}

function Item({ to, icon, children, end }: { to: string; icon: ReactNode; children: ReactNode; end?: boolean }) {
  return (
    <NavLink to={to} end={end} className={({ isActive }) => cx(isActive && "active")}>
      {icon}
      <span>{children}</span>
    </NavLink>
  );
}

/** For a Free company: the way to the plans (Lux & billing › upgrade section). */
function UpgradeNav({ orgId }: { orgId: string }) {
  const plan = useOrgPlan(orgId, false);
  const t = useT();
  if (plan.data?.plan !== "free") return null;
  return (
    <NavLink to="/lux#upgrade" className="upgrade-nav">
      <Sparkles /> {t("shell.upgrade")}
    </NavLink>
  );
}

function LuxPill({ orgId }: { orgId: string }) {
  const wallet = useLuxWallet(orgId);
  const t = useT();
  const w = wallet.data;
  return (
    <NavLink to="/lux" className="credit-pill" title={w ? t("shell.lux.designs", { count: w.buys.designs }) : undefined}>
      <LuxCoin size={20} />
      <div>
        <b>{w ? fmtLux(w.balance) : "—"}</b>
        <span> Lux</span>
      </div>
    </NavLink>
  );
}

/** The running dashboard build, and for platform admins the API build too (from GET /api/health). */
function BuildStamp({ withApi }: { withApi: boolean }) {
  const [api, setApi] = useState<string | null>(null);
  useEffect(() => {
    if (!withApi) return;
    let live = true;
    fetch(`${config.apiUrl}/api/health`)
      .then((r) => (r.ok ? r.json() : null))
      .then((h) => live && h?.version && setApi(versionLabel(h.version)))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [withApi]);
  const built = APP_BUILT_AT ? `, built ${APP_BUILT_AT.replace("T", " ").replace("Z", " UTC")}` : "";
  const title = `Dashboard ${APP_VERSION}${APP_COMMIT ? ` (${APP_COMMIT})` : ""}${built}`;
  return (
    <div className="build-stamp" title={title}>
      {versionLabel()}
      {api && <> · API {api}</>}
    </div>
  );
}

/** The active company, and a menu of the user's other companies (each request names the chosen one). */
function CompanySwitcher() {
  const { memberships, membership, orgName, role, adminView, selectOrg, orgId } = useOrg();
  const company = useOrganization(orgId ?? ""); // the shell's ["org", id] query (usePartnersEnabled): no extra call
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const t = useT();
  const roleName = (r: string | null | undefined) => (r ? t(`shell.role.${r}` as "shell.role.owner") : "");
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [open]);
  return (
    <div className="company-switch" onClick={(e) => e.stopPropagation()}>
      <button className="org-switch" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <div className="org-avatar">{initials(orgName)}</div>
        <div className="org-meta">
          <b>{orgName}</b>
          <span>{adminView ? t("shell.company.adminView") : membership ? roleName(role) : ""}</span>
          <StatusBadge badge={orgId ? company.data?.badge : null} size="sm" />
        </div>
        <ChevronsUpDown size={16} className="switch-caret" />
      </button>
      {open && (
        <div className="company-menu" role="menu">
          <div className="company-menu-head">{t("shell.company.yours")}</div>
          {memberships.map((m) => (
            <button
              key={m.organization_id}
              role="menuitem"
              className={cx("company-item", m.organization_id === orgId && "current")}
              onClick={() => {
                setOpen(false);
                if (m.organization_id !== orgId) {
                  selectOrg(m.organization_id);
                  navigate("/designs");
                }
              }}
            >
              <div className="org-avatar small">{initials(m.organization_name)}</div>
              <span className="company-name">
                <span className="ellipsis" title={m.organization_name}>{m.organization_name}</span>
                <small>{roleName(m.role)}</small>
              </span>
              {m.organization_id === orgId ? <Check size={15} /> : <span style={{ width: 15 }} />}
            </button>
          ))}
          <div className="company-menu-foot">
            <button className="company-item" onClick={() => navigate("/onboarding?new")}>
              <Plus size={15} /> <span>{t("shell.company.create")}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function AdminViewBanner() {
  const { orgName, orgId, exitAdminView } = useOrg();
  const navigate = useNavigate();
  const t = useT();
  return (
    <div className="admin-banner" role="status">
      <ShieldAlert size={18} />
      <span>
        <FormattedMessage id="shell.adminBanner.text" values={{ org: orgName, b: (chunks) => <b>{chunks}</b> }} />
      </span>
      <Button
        size="sm"
        onClick={() => {
          exitAdminView();
          navigate(orgId ? `/admin/orgs/${orgId}` : "/admin/orgs");
        }}
      >
        {t("shell.adminBanner.exit")}
      </Button>
    </div>
  );
}

/** Admin menu group "Library" (pages in src/library; their content comes from the API). */
function LibraryNav() {
  const t = useT();
  return (
    <>
      <div className="nav-section">{t("shell.nav.section.library")}</div>
      <Item to="/admin/library" end icon={<BookOpen />}>{t("shell.nav.allPages")}</Item>
      {LIBRARY_MENU.map(([to, label, icon]) => (
        <Item key={to} to={to} icon={icon}>{label}</Item>
      ))}
    </>
  );
}

function Shell({ admin }: { admin?: boolean }) {
  const { email, signOut } = useAuth();
  const { me, isPlatformAdmin, orgId, adminView, can } = useOrg();
  // Off by default; Solar OS switches it on per company. Off: no menu item (the page explains, if opened directly).
  const partnersEnabled = usePartnersEnabled(admin ? null : orgId);
  const [theme, toggleTheme] = useTheme();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const t = useT();
  const company = useOrganization(admin ? "" : orgId ?? "");
  useLocaleSync(me.preferences?.locale, company.data?.main_locale);
  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <div className={cx("shell", open && "nav-open")}>
      <aside className="sidebar" aria-label={t("shell.nav.main")}>
        <Brand sub={admin ? t("shell.nav.superAdmin") : undefined} />
        {!admin && orgId && <CompanySwitcher />}
        {admin ? (
          <nav className="nav">
            <div className="nav-section">{t("shell.nav.section.platform")}</div>
            <Item to="/admin/orgs" icon={<Building2 />}>{t("shell.nav.organisations")}</Item>
            <Item to="/admin/audit-log" icon={<History />}>{t("shell.nav.auditLog")}</Item>
            <Item to="/admin/users" icon={<UserSearch />}>{t("shell.nav.users")}</Item>
            <Item to="/admin/trials" icon={<Inbox />}>{t("shell.nav.trialRequests")}</Item>
            <Item to="/admin/emails" icon={<Mail />}>{t("shell.nav.emails")}</Item>
            <div className="nav-section">{t("shell.nav.section.configuration")}</div>
            <Item to="/admin/settings" icon={<Gauge />}>{t("shell.nav.platformSettings")}</Item>
            <Item to="/admin/plans" icon={<Layers />}>{t("shell.nav.plansLimits")}</Item>
            <Item to="/admin/coupons" icon={<TicketPercent />}>{t("shell.nav.coupons")}</Item>
            <Item to="/admin/tariffs" icon={<Zap />}>{t("shell.nav.electricityPrices")}</Item>
            <Item to="/admin/permit-equipment" icon={<Cable />}>{t("shell.nav.permitEquipment")}</Item>
            <Item to="/admin/permit-ahjs" icon={<Landmark />}>{t("shell.nav.permitAhjs")}</Item>
            <Item to="/admin/stages" icon={<Workflow />}>{t("shell.nav.pipelineStages")}</Item>
            <Item to="/admin/integrations" icon={<Blocks />}>{t("shell.nav.integrations")}</Item>
            <Item to="/admin/migrations" icon={<Import />}>{t("shell.nav.imports")}</Item>
            <Item to="/admin/partners" icon={<Handshake />}>{t("shell.nav.partners")}</Item>
            <Item to="/admin/designs/clear" icon={<RefreshCcw />}>{t("shell.nav.clearDesigns")}</Item>
            <Item to="/admin/cache" icon={<Database />}>{t("shell.nav.providerCache")}</Item>
            <LibraryNav />
            {orgId && !adminView && (
              <>
                <div className="nav-section">{t("shell.nav.section.switch")}</div>
                <Item to="/designs" icon={<DirIcon><ArrowLeftRight /></DirIcon>}>{t("shell.nav.companyDashboard")}</Item>
              </>
            )}
          </nav>
        ) : (
          <nav className="nav">
            <div className="nav-section">{t("shell.nav.section.sales")}</div>
            <Item to="/get-started" icon={<Rocket />}>{t("shell.nav.getStarted")}</Item>
            <Item to="/designs" icon={<LayoutGrid />}>{t("shell.nav.designsLeads")}</Item>
            {can("designs_leads", "view") && <Item to="/designer" icon={<PenTool />}>{t("shell.nav.designer")}</Item>}
            <div className="nav-section">{t("shell.nav.section.website")}</div>
            <Item to="/widget" icon={<Code2 />}>{t("shell.nav.widget")}</Item>
            <Item to="/branding" icon={<Palette />}>{t("shell.nav.branding")}</Item>
            <Item to="/rules" icon={<SlidersHorizontal />}>{t("shell.nav.rules")}</Item>
            <Item to="/electricity-prices" icon={<Zap />}>{t("shell.nav.electricityPrices")}</Item>
            {partnersEnabled && <Item to="/financing-suppliers" icon={<Handshake />}>{t("shell.nav.financingSuppliers")}</Item>}
            {can("integrations", "view") && <Item to="/integrations" icon={<Blocks />}>{t("shell.nav.integrations")}</Item>}
            {can("settings_rules", "view") && <Item to="/solarapp" icon={<FileCheck2 />}>SolarAPP+</Item>}
            <div className="nav-section">{t("shell.nav.section.account")}</div>
            <Item to="/lux" icon={<LuxGlyph />}>{t("shell.nav.luxBilling")}</Item>
            <Item to="/team" icon={<Users />}>{t("shell.nav.team")}</Item>
            {can("audit_log", "view") && <Item to="/audit-log" icon={<History />}>{t("shell.nav.auditLog")}</Item>}
            {can("team", "edit") && config.identityUrl && <Item to="/sso" icon={<ShieldCheck />}>{t("shell.nav.sso")}</Item>}
            {isPlatformAdmin && (
              <>
                <div className="nav-section">Solar OS</div>
                <Item to="/admin/orgs" icon={<FileClock />}>{t("shell.nav.superAdmin")}</Item>
              </>
            )}
          </nav>
        )}
        <div className="sidebar-foot">
          {!admin && orgId && <LuxPill orgId={orgId} />}
          {!admin && orgId && <UpgradeNav orgId={orgId} />}
          <div className="user-row">
            <span className="email" dir="auto" title={me.user.email}>{email || me.user.email}</span>
            <LanguagePicker me={me} companyDefault={company.data?.main_locale} compact />
            <HelpLink label={t("shell.help")} />
            <button className="icon-btn" onClick={toggleTheme} aria-label={t("shell.theme.toggle")} title={t("shell.theme.toggle")}>
              {theme === "dark" ? <Sun /> : <Moon />}
            </button>
            <button className="icon-btn" onClick={() => signOut()} aria-label={t("shell.signOut")} title={t("shell.signOut")}>
              <DirIcon><LogOut /></DirIcon>
            </button>
          </div>
          <BuildStamp withApi={isPlatformAdmin} />
        </div>
      </aside>
      <div className="main">
        <header className="topbar">
          <button className="icon-btn" onClick={() => setOpen(true)} aria-label={t("shell.menu.open")}>
            <Menu />
          </button>
          <Brand />
        </header>
        {!admin && adminView && <AdminViewBanner />}
        <main className="content">
          <NamePrompt />
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export function OrgLayout() {
  return <Shell />;
}

export function AdminLayout() {
  return <Shell admin />;
}
